<template>
    <div class="is-flex is-flex-direction-column has-gap-large is-width-100">
        <div class="box is-flex is-flex-direction-column has-gap-large">
            <div class="box-title m-0">
                Order #{{ fulfillmentOrder.orderId }}
            </div>

            <AppOrder
                :order="fulfillmentOrder.order"
                hide-header
                hide-footer
            />

            <div
                class="block is-flex is-align-items-baseline is-size-6 is-flex-wrap-wrap"
                style="row-gap: .5rem; column-gap: 3rem;"
            >
                <span
                    class="tag is-capitalized"
                    :class="orderStatus.colorClass"
                >
                    {{ orderStatus.text }}
                </span>
            </div>
        </div>

        <div class="box is-flex is-flex-direction-column has-gap-large">
            <div class="is-flex is-justify-content-space-between is-align-content-center">
                <div class="box-title mb-0">
                    Fulfillment Dashboard
                </div>
                <div>
                    <button
                        v-if="!fulfillmentOrder.datePostagePurchased"
                        class="button is-small is-warning"
                        @click.prevent="showShipmentDetailsModal = true"
                    >
                        Set Shipment Details
                    </button>
                </div>
            </div>

            <template v-if="fulfillmentOrder.datePostagePurchased">
                <a
                    class="button is-success is-outlined is-width-fit"
                    :href="fulfillmentOrder.labelUrl"
                    target="_blank"
                >
                    View Shipping Label
                </a>
            </template>

            <template v-else-if="hasShipmentDetails">
                <div class="is-flex is-flex-wrap-wrap has-gap-medium">
                    <EasyPostRateCard
                        v-for="rate in sortedRates"
                        @select="ratePendingPurchase = rate"
                        :rate="rate"
                    />
                </div>
            </template>

            <template v-else>
                <p>
                    You must set shipment details before you can view fulfillment options.
                </p>
            </template>
        </div>

        <FulfillmentOrderShipmentDetailsModal
            :open="showShipmentDetailsModal"
            :fulfillmentOrder="fulfillmentOrder"
            @close="showShipmentDetailsModal = false"
        />

        <FulfillmentOrderPurchaseConfirmationModal
            :open="!!ratePendingPurchase"
            :rate="ratePendingPurchase"
            :fulfillmentOrderId="fulfillmentOrder.id"
            @close="ratePendingPurchase = null"
        />

    </div>
</template>

<script setup lang="ts">
import Base from "@/Layouts/Base.vue";
import Default from "@/Layouts/Default.vue";
import {FulfillmentOrder} from "@/Types/FulfillmentOrder";
import AppOrder from "@/Components/AppOrder.vue";
import {computed, ref} from "vue";
import FulfillmentOrderShipmentDetailsModal from "@/Components/FulfillmentOrderShipmentDetailsModal.vue";
import type {IRate, Shipment} from "@easypost/api/types/Shipment";
import EasyPostRateCard from "@/Components/EasyPostRateCard.vue";
import FulfillmentOrderPurchaseConfirmationModal from "@/Components/FulfillmentOrderPurchaseConfirmationModal.vue";

defineOptions({
    layout: [Base, Default]
});

const props = defineProps<{
    fulfillmentOrder: FulfillmentOrder,
    shipment: Shipment,
}>();

const ratePendingPurchase = ref<IRate|null>(null);

const orderStatus = computed<{text: string, colorClass: string}>(() => {
    if (props.fulfillmentOrder.dateShipped) {
        return {text: 'Shipped', colorClass: 'is-success'};
    } else if (props.fulfillmentOrder.datePostagePurchased) {
        return {text: 'Pending Shipment', colorClass: 'is-info'};
    } else {
        return { text: 'Pending Postage', colorClass: 'is-warning' };
    }
});

const showShipmentDetailsModal = ref(false);

const hasShipmentDetails = computed(() => {
    return ((props.fulfillmentOrder.parcelWidth?.toString?.() ?? '') !== '')
        && ((props.fulfillmentOrder.parcelLength?.toString?.() ?? '') !== '')
        && ((props.fulfillmentOrder.parcelHeight?.toString?.() ?? '') !== '')
        && ((props.fulfillmentOrder.parcelOunces?.toString?.() ?? '') !== '');
});

const sortedRates = computed(() => {
    return props.shipment.rates.sort((a, b) => {
        if (props.shipment.selected_rate?.id === a.id) {
            return -1;
        } else if (props.shipment.selected_rate?.id === b.id) {
            return 1;
        } else if (Number(a.rate) < Number(b.rate)) {
            return -1;
        } else if (Number(a.rate) > Number(b.rate)) {
            return 1;
        } else {
            return 0;
        }
    });
});

</script>
